/* eslint-disable no-console */

import { PersistedSession } from './dataModels/PersistedSession';
import { SessionCookie } from './dataModels/SessionCookie';
import { SsoPortalEligibilityData } from './dataModels/SsoPortalEligibilityData';
import { PORTAL_VALUE_UNKNOWN } from './constants';

export const SSO_SESSION_COOKIE_NAME = 'FINDCARE_AND_COST_SSO_SESSION';
export const SESSION_DURATION_SECS = 60 * 20; // 20 minutes
export const SESSION_TABLE_NAME = 'sso-sessions-multiple';
export const DYNAMO_DB_VERSION = '2012-08-10';
export const SESSION_COOKIE_PROPS = 'Path=/; Secure';
export const CREATE_SESSION_ACTION_TYPE_CREATE = 'create';
export const CREATE_SESSION_ACTION_TYPE_DROP = 'drop';
export const OPTUM_CORRELATION_ID_HEADER = 'optum-cid-ext';

export const getSessionDurationSecs = (env): number => {
  return !isNaN(env.SESSION_DURATION_SECS) ? parseInt(env.SESSION_DURATION_SECS) : SESSION_DURATION_SECS;
};

export const constructSessionValue = (memberId, sessionId, lastAccessedTimeSecs, durationSecs) => {
  return `${memberId}!${sessionId}!${lastAccessedTimeSecs}!${durationSecs}`;
};

export const constructSessionCookie = (sessionValue, durationSecs, domain) => {
  const domainProp = domain ? `Domain=${domain}; ` : '';
  return `${SSO_SESSION_COOKIE_NAME}=${sessionValue}; ${domainProp}${SESSION_COOKIE_PROPS}; Max-Age=${durationSecs}`;
};

export const parseSessionCookie = (sessionValue): SessionCookie => {
  const parts = sessionValue?.split('!');
  return {
    memberId: parts?.[0],
    sessionId: parts?.[1],
    lastAccessedSecs: !isNaN(parts?.[2]) ? parseInt(parts[2]) : 0,
    durationSecs: !isNaN(parts?.[3]) ? parseInt(parts[3]) : 0,
  };
};

export const isSessionExpired = (ttlSec: number, currentTimeSecs) => {
  return ttlSec <= currentTimeSecs;
};

export const createSession = async (ddb, token, updatedTtl, memberId, ssoAttributes) => {
  const serializedSsoAttributes = JSON.stringify(ssoAttributes);
  const putItemParams = {
    TableName: SESSION_TABLE_NAME,
    Item: {
      sessionId: { S: token },
      memberId: { S: memberId },
      timeToLive: { N: updatedTtl.toString() },
      ssoAttributes: { S: serializedSsoAttributes },
    },
  };
  return await ddb.putItem(putItemParams).promise();
};

export const updateSession = async (ddb, token, updatedTtl) => {
  const updateItemParams = {
    TableName: SESSION_TABLE_NAME,
    Key: {
      sessionId: { S: token },
    },
    UpdateExpression: 'SET timeToLive = :ttl', // timeToLive is the colName in DynamoDB, :ttl is the alias
    ExpressionAttributeValues: {
      ':ttl': { N: updatedTtl.toString() },
    },
  };
  return await ddb.updateItem(updateItemParams).promise();
};

export const dropSession = async (ddb, token) => {
  const deleteItemParams = {
    TableName: SESSION_TABLE_NAME,
    Key: {
      sessionId: { S: token },
    },
  };
  return await ddb.deleteItem(deleteItemParams).promise();
};

export const getSession = async (ddb, token): Promise<PersistedSession> => {
  const getItemParams = {
    TableName: SESSION_TABLE_NAME,
    Key: {
      sessionId: { S: token },
    },
  };
  const storedSession = await ddb.getItem(getItemParams).promise();

  const ttlSecsStr = storedSession?.Item?.timeToLive?.N;
  const ttlSecs = !isNaN(ttlSecsStr) ? parseInt(ttlSecsStr) : 0;

  const sessionAttributesStr = storedSession?.Item?.ssoAttributes?.S;
  const ssoAttributes = sessionAttributesStr ? JSON.parse(sessionAttributesStr) : {};
  return {
    sessionId: storedSession?.Item?.sessionId?.S,
    memberId: storedSession?.Item?.memberId?.S,
    ttlSecs,
    ssoAttributes,
  };
};

export const formatDateByYearMonthDay = (inputDate: string) => {
  const dateObj = new Date(inputDate);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const constructResponse = (callback, statusCode, correlationId, sessionCookie?, data?) => {
  let headers = {};

  if (correlationId) {
    headers = { ...headers, [OPTUM_CORRELATION_ID_HEADER]: correlationId };
  }

  if (sessionCookie) {
    headers = { ...headers, 'Set-Cookie': sessionCookie };
  }

  let responseBody = {};
  responseBody = {
    ...responseBody,
    statusCode,
    headers,
  };

  if (data) {
    responseBody = { ...responseBody, body: JSON.stringify(data) };
  }
  return callback(null, responseBody);
};

export const extractCookieDomainFromRequest = (event, env): string => {
  const referer = event?.headers?.Referer || event?.headers?.referer;
  console.log(`Referer: ${referer}`);

  const allowedDomains = env.ALLOWED_DOMAINS.split(',').map((allowedDomain) => allowedDomain.trim());

  const urlObj = new URL(referer);
  const refererHostname = urlObj.hostname || '';

  for (const allowedDomain of allowedDomains) {
    if (refererHostname.includes(allowedDomain)) {
      return `.${allowedDomain}`;
    }
  }
  throw new Error(`Referer domain (${refererHostname}) not found in allowed domains`);
};

export const getPortalSource = (portalEligibilityData: SsoPortalEligibilityData): string => {
  const portalSource = portalEligibilityData?.portalSource;
  if (portalSource) {
    return portalSource;
  }
  return PORTAL_VALUE_UNKNOWN;
};

export const getUserType = (portalEligibilityData: SsoPortalEligibilityData): string => {
  const userType = portalEligibilityData?.callingUser;
  if (userType) {
    return userType;
  }
  return PORTAL_VALUE_UNKNOWN;
};
